/**
 * Pure workflow value resolution, token formatting, merge-HTML rendering of workflow tags, settings/flags collection and
 * condition evaluation, shared by `jms-api` (`test-step`, `test-workflow`) and `jms-web` (merge text previews, condition sample
 * checks, step sheet Data/Result panes). A text value resolves to merge HTML with its workflow tags rendered and its record tags
 * left as spans for the server's merge renderer (see `workflow.merge.html.ts`).
 * Mirrors `jms-extension-runtime/cells/entity.workflow/entity.workflow.values.ts` so the editor and the runtime always evaluate
 * tokens, lists, built-in sources and operators with identical rules; `tests/workflow.evaluator.fixtures.ts` is the conformance
 * table both sides must pass, so a rule changed here changes that table and the runtime with it.
 */
import type { AgentJobContext, ExtensionRunTrigger } from './agent.run.context';
import { WorkflowOperator, WorkflowStepType, WorkflowValueSource, type WorkflowComparison, type WorkflowCondition, type WorkflowDefinition, type WorkflowStep, type WorkflowTokenFormat, type WorkflowValue, type WorkflowValueFormatOptions } from './workflow.definition';
import { escapeMergeText, workflowMergeTags } from './workflow.merge.html';

const DATE_FORMATS = new Set<WorkflowTokenFormat>(['date', 'dateNumeric', 'dateLong', 'dateTime', 'time']);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
/** The operators that compare calendar days in the tenant's time zone, so they read the `timeZone` setting. */
const DAY_OPERATORS = new Set<WorkflowOperator>([WorkflowOperator.IsOn, WorkflowOperator.Before, WorkflowOperator.After]);

/** What a workflow value may read at one point of a run or editor preview. */
export interface WorkflowValueScope {
    /** The run's trigger data (`job.<path>` and `trigger.<path>`). */
    trigger: { job: AgentJobContext; trigger: ExtensionRunTrigger; workflow?: { name?: string }; run?: { startedAt?: string } };
    /** Outputs of the actions that ran so far, by step id; inside a loop, the current iteration's outputs win. */
    steps: ReadonlyMap<string, Record<string, unknown>>;
    /** The current loop item; undefined outside a loop. */
    item?: unknown;
    /** The settings the definition reads, by key, as WorkBuddy projected them. */
    settings: Readonly<Record<string, unknown>>;
    /** The flags the definition reads, by flag. */
    flags: Readonly<Record<string, boolean>>;
    /** The walk's variables by name. */
    vars: Map<string, unknown>;
    /** Optional workflow metadata for the `workflow.name` built-in. */
    workflow?: { name?: string };
    /** Optional run metadata for the `run.startedAt` built-in. */
    run?: { startedAt?: string };
    /** Optional deterministic timestamp for `{ source: 'now' }` and `trigger.now`. */
    now?: string;
}

/** The settings and flags a definition reads; collected by `collectWorkflowReads`. */
export interface WorkflowReads {
    settings: string[];
    flags: string[];
}

/** A condition's result with one line per comparison, for run history and editor sample checks. */
export interface WorkflowConditionEvaluation {
    result: boolean;
    explanation: string[];
}

/** Resolves `value` in `scope`; text resolves to its merge HTML with the workflow tags rendered and the record tags left. */
export function resolveWorkflowValue(value: WorkflowValue, scope: WorkflowValueScope): unknown {

    switch (value.source) {
        case WorkflowValueSource.Literal:
            return value.value;
        case WorkflowValueSource.List:
            return value.items
                .flatMap(item => {
                    const resolved = resolveWorkflowValue(item, scope);
                    return Array.isArray(resolved) ? resolved : [resolved];
                })
                .filter(item => item !== null && item !== undefined && item !== '');
        case WorkflowValueSource.Now:
            return applyFormatOptions(resolveNow(scope), value, scope);
        case WorkflowValueSource.Trigger:
            return applyFormatOptions(resolveTriggerPath(value.path, scope), value, scope);
        case WorkflowValueSource.Step:
            return applyFormatOptions(readPath(scope.steps.get(value.stepId) ?? null, value.path), value, scope);
        case WorkflowValueSource.Item:
            return applyFormatOptions(readPath(scope.item ?? null, value.path), value, scope);
        case WorkflowValueSource.Text:
            return renderWorkflowMergeHtml(value.html, scope);
        case WorkflowValueSource.Setting:
            return applyFormatOptions(readPath(scope.settings[value.key] ?? null, value.path), value, scope);
        case WorkflowValueSource.Flag:
            return applyFormatOptions(scope.flags[value.flag] === true, value, scope);
        case WorkflowValueSource.Variable:
            return applyFormatOptions(scope.vars.get(value.name) ?? null, value, scope);
    }
}

function resolveRawValue(value: WorkflowValue, scope: WorkflowValueScope): unknown {

    switch (value.source) {
        case WorkflowValueSource.Literal:
        case WorkflowValueSource.List:
        case WorkflowValueSource.Text:
            return resolveWorkflowValue(value, scope);
        case WorkflowValueSource.Now:
            return resolveNow(scope);
        case WorkflowValueSource.Trigger:
            return resolveTriggerPath(value.path, scope);
        case WorkflowValueSource.Step:
            return readPath(scope.steps.get(value.stepId) ?? null, value.path);
        case WorkflowValueSource.Item:
            return readPath(scope.item ?? null, value.path);
        case WorkflowValueSource.Setting:
            return readPath(scope.settings[value.key] ?? null, value.path);
        case WorkflowValueSource.Flag:
            return scope.flags[value.flag] === true;
        case WorkflowValueSource.Variable:
            return scope.vars.get(value.name) ?? null;
    }
}

/**
 * `html` with every workflow tag replaced by its value as escaped text (formatted by the tag's `data-wb-*` options, line breaks as
 * `<br>`); record tags stay as they are for the server's merge renderer. Called for every text value, and by the editor's preview
 * before it asks the server to render the record tags on the sample record.
 */
export function renderWorkflowMergeHtml(html: string, scope: WorkflowValueScope): string {

    let rendered = '';
    let lastIndex = 0;
    for (const tag of workflowMergeTags(html)) {
        if (!tag.value) {
            continue;
        }
        const text = formatWorkflowTokenText(resolveRawValue(tag.value, scope), tag, scope);
        rendered += html.slice(lastIndex, tag.start) + escapeMergeText(text).replace(/\r\n|\r|\n/g, '<br>');
        lastIndex = tag.end;
    }
    return rendered + html.slice(lastIndex);
}

function resolveTriggerPath(path: string, scope: WorkflowValueScope): unknown {

    if (path === 'workflow.name') {
        return scope.workflow?.name ?? scope.trigger.workflow?.name ?? null;
    }
    if (path === 'run.startedAt') {
        return scope.run?.startedAt ?? scope.trigger.run?.startedAt ?? scope.trigger.trigger?.lastAt ?? scope.trigger.trigger?.firstAt ?? null;
    }
    if (path === 'now') {
        return resolveNow(scope);
    }
    return readPath(scope.trigger, path);
}

function resolveNow(scope: WorkflowValueScope): string | null {

    return scope.now ?? scope.run?.startedAt ?? scope.trigger.run?.startedAt ?? scope.trigger.trigger?.lastAt ?? scope.trigger.trigger?.firstAt ?? null;
}

function applyFormatOptions(raw: unknown, options: WorkflowValueFormatOptions, scope: WorkflowValueScope): unknown {

    if (options.format === undefined && options.decimals === undefined && options.ifEmpty === undefined && options.join === undefined) {
        return raw;
    }
    return formatWorkflowTokenText(raw, options, scope);
}

/** Formats a resolved token value into display text using `options` and the tenant's `timeZone` setting when present. */
export function formatWorkflowTokenText(raw: unknown, options: WorkflowValueFormatOptions, scope: WorkflowValueScope): string {

    if (raw === null || raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)) {
        return options.ifEmpty ?? '';
    }
    if (Array.isArray(raw)) {
        const rendered = raw.map(item => textOf(item)).filter(Boolean);
        return rendered.length ? rendered.join(options.join ?? ', ') : (options.ifEmpty ?? '');
    }
    if (options.format) {
        if (DATE_FORMATS.has(options.format)) {
            const formatted = formatDatePreset(raw, options.format, validTimeZone(scope.settings.timeZone));
            if (formatted !== null) {
                return formatted;
            }
        }
        if (options.format === 'number' || options.format === 'currency') {
            const num = typeof raw === 'number' ? raw : Number(raw);
            if (Number.isFinite(num)) {
                if (options.format === 'currency') {
                    const decimals = options.decimals ?? 2;
                    return `$${new Intl.NumberFormat('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(num)}`;
                }
                return new Intl.NumberFormat(
                    'en-AU',
                    options.decimals !== undefined ? { minimumFractionDigits: options.decimals, maximumFractionDigits: options.decimals } : undefined,
                ).format(num);
            }
        }
    }
    const text = textOf(raw);
    return text || (options.ifEmpty ?? '');
}

function formatDatePreset(raw: unknown, format: WorkflowTokenFormat, timeZone: string): string | null {

    if (typeof raw !== 'string' && typeof raw !== 'number') {
        return null;
    }
    const ms = typeof raw === 'number' ? raw : Date.parse(raw);
    if (Number.isNaN(ms)) {
        return null;
    }
    const date = new Date(ms);
    switch (format) {
        case 'date':
            return new Intl.DateTimeFormat('en-AU', { timeZone, day: 'numeric', month: 'short', year: 'numeric' }).format(date);
        case 'dateNumeric':
            return new Intl.DateTimeFormat('en-AU', { timeZone, day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
        case 'dateLong':
            return new Intl.DateTimeFormat('en-AU', { timeZone, weekday: 'long', day: 'numeric', month: 'long' }).format(date).replace(/,/g, '');
        case 'dateTime':
            return new Intl.DateTimeFormat('en-AU', { timeZone, day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
        case 'time':
            return new Intl.DateTimeFormat('en-AU', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
        default:
            return null;
    }
}

function validTimeZone(candidate: unknown): string {

    if (typeof candidate === 'string' && candidate.trim()) {
        try {
            Intl.DateTimeFormat('en-AU', { timeZone: candidate.trim() });
            return candidate.trim();
        } catch {
            return 'UTC';
        }
    }
    return 'UTC';
}

/** Collects every setting key and feature flag `definition` reads, in first-seen order. */
export function collectWorkflowReads(definition: WorkflowDefinition): WorkflowReads {

    const settings = new Set<string>();
    const flags = new Set<string>();
    const checkFormat = (holder: WorkflowValueFormatOptions): void => {

        if (holder.format && DATE_FORMATS.has(holder.format)) {
            settings.add('timeZone');
        }
    };
    const visit = (value: WorkflowValue | undefined): void => {

        if (!value) {
            return;
        }
        if ('format' in value) {
            checkFormat(value);
        }
        if (value.source === WorkflowValueSource.Setting) {
            settings.add(value.key);
        }
        if (value.source === WorkflowValueSource.Flag) {
            flags.add(value.flag);
        }
        if (value.source === WorkflowValueSource.List) {
            value.items.forEach(visit);
            return;
        }
        if (value.source !== WorkflowValueSource.Text) {
            return;
        }
        for (const tag of workflowMergeTags(value.html)) {
            checkFormat(tag);
            visit(tag.value ?? undefined);
        }
    };
    const visitCondition = (condition: WorkflowCondition): void => {

        if ('all' in condition || 'any' in condition) {
            ('all' in condition ? condition.all : condition.any).forEach(visitCondition);
            return;
        }
        if ('not' in condition) {
            visitCondition(condition.not);
            return;
        }
        if (DAY_OPERATORS.has(condition.operator)) {
            settings.add('timeZone');
        }
        visit(condition.left);
        visit(condition.right);
    };
    const visitSteps = (steps: WorkflowStep[]): void => {

        for (const step of steps) {
            switch (step.type) {
                case WorkflowStepType.Action:
                    Object.values(step.input).forEach(visit);
                    break;
                case WorkflowStepType.If:
                    visitCondition(step.condition);
                    visitSteps(step.then);
                    visitSteps(step.else);
                    break;
                case WorkflowStepType.Switch:
                    visit(step.value);
                    step.cases.forEach(item => visitSteps(item.steps));
                    visitSteps(step.default);
                    break;
                case WorkflowStepType.ForEach:
                    visit(step.items);
                    visitSteps(step.steps);
                    break;
                case WorkflowStepType.Stop:
                    visit(step.reason);
                    break;
                case WorkflowStepType.SetVariable:
                    visit(step.value);
                    break;
            }
        }
    };
    visitSteps(definition.steps);
    return { settings: [...settings], flags: [...flags] };
}

/** Evaluates `condition` in `scope`; `all` and `any` evaluate every member so the explanation is complete. */
export function evaluateWorkflowCondition(condition: WorkflowCondition, scope: WorkflowValueScope): WorkflowConditionEvaluation {

    if ('all' in condition || 'any' in condition) {
        const all = 'all' in condition;
        const members = (all ? condition.all : condition.any).map(member => evaluateWorkflowCondition(member, scope));
        return {
            result: all
                ? members.every(member => member.result)
                : members.some(member => member.result),
            explanation: members.flatMap(member => member.explanation),
        };
    }
    if ('not' in condition) {
        const inner = evaluateWorkflowCondition(condition.not, scope);
        return {
            result: !inner.result,
            explanation: inner.explanation.map(line => `not (${line})`),
        };
    }
    return compare(condition, scope);
}

function compare(comparison: WorkflowComparison, scope: WorkflowValueScope): WorkflowConditionEvaluation {

    const left = resolveWorkflowValue(comparison.left, scope);
    const right = comparison.right ? resolveWorkflowValue(comparison.right, scope) : undefined;
    const result = applyOperator(comparison.operator, left, right, scope);
    const operand = comparison.right
        ? ` ${JSON.stringify(right ?? null)}`
        : '';
    return {
        result,
        explanation: [`${describe(comparison.left)} (${JSON.stringify(left ?? null)}) ${comparison.operator}${operand}: ${result}`],
    };
}

function applyOperator(operator: WorkflowOperator, left: unknown, right: unknown, scope: WorkflowValueScope): boolean {

    switch (operator) {
        case WorkflowOperator.Equals:
            return sameWorkflowValue(left, right);
        case WorkflowOperator.NotEquals:
            return !sameWorkflowValue(left, right);
        case WorkflowOperator.IsSet:
            return isSet(left);
        case WorkflowOperator.IsNotSet:
            return !isSet(left);
        case WorkflowOperator.Contains:
            if (Array.isArray(left)) {
                return left.some(item => sameWorkflowValue(item, right));
            }
            return typeof left === 'string' && typeof right === 'string' && left.toLowerCase().includes(right.toLowerCase());
        case WorkflowOperator.NotContains:
            if (Array.isArray(left)) {
                return !left.some(item => sameWorkflowValue(item, right));
            }
            return typeof left === 'string' && typeof right === 'string' && !left.toLowerCase().includes(right.toLowerCase());
        case WorkflowOperator.GreaterThan:
            return compareOrdered(left, right, (a, b) => a > b);
        case WorkflowOperator.LessThan:
            return compareOrdered(left, right, (a, b) => a < b);
        case WorkflowOperator.In:
            return isAnyOf(left, right);
        case WorkflowOperator.NotIn:
            return !isAnyOf(left, right);
        case WorkflowOperator.IsOn:
            return compareDays(left, right, scope, (a, b) => a === b);
        case WorkflowOperator.Before:
            return compareDays(left, right, scope, (a, b) => a < b);
        case WorkflowOperator.After:
            return compareDays(left, right, scope, (a, b) => a > b);
    }
}

function compareOrdered(left: unknown, right: unknown, predicate: (a: number | string, b: number | string) => boolean): boolean {

    if (typeof left === 'number' && typeof right === 'number') {
        return predicate(left, right);
    }
    if (typeof left === 'string' && typeof right === 'string') {
        if (ISO_DATE.test(left) && ISO_DATE.test(right)) {
            const leftMs = Date.parse(left);
            const rightMs = Date.parse(right);
            if (!Number.isNaN(leftMs) && !Number.isNaN(rightMs)) {
                return predicate(leftMs, rightMs);
            }
        }
        return predicate(left, right);
    }
    return false;
}

/** Compares the calendar days of two dates in the tenant's time zone; false when either side is not a date. */
function compareDays(left: unknown, right: unknown, scope: WorkflowValueScope, predicate: (a: string, b: string) => boolean): boolean {

    const timeZone = validTimeZone(scope.settings.timeZone);
    const leftDay = calendarDay(left, timeZone);
    const rightDay = calendarDay(right, timeZone);
    if (leftDay === null || rightDay === null) {
        return false;
    }
    // `YYYY-MM-DD` orders as text exactly as the days order.
    return predicate(leftDay, rightDay);
}

function calendarDay(value: unknown, timeZone: string): string | null {

    if (typeof value !== 'string' && typeof value !== 'number') {
        return null;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }
    const ms = typeof value === 'number' ? value : Date.parse(value);
    if (Number.isNaN(ms)) {
        return null;
    }
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
}

function isAnyOf(left: unknown, right: unknown): boolean {

    const values = Array.isArray(left) ? left : [left];
    return listOf(right).some(candidate => values.some(value => sameWorkflowValue(value, candidate)));
}

function listOf(value: unknown): unknown[] {

    if (Array.isArray(value)) {
        return value;
    }
    if (value === null || value === undefined) {
        return [];
    }
    return [value];
}

/** Equality of scalars by value; objects compare by their `_id` or `id`, so an embedded entity or stage item equals its id. */
export function sameWorkflowValue(left: unknown, right: unknown): boolean {

    return idOf(left) === idOf(right);
}

function idOf(value: unknown): unknown {

    if (typeof value === 'object' && value !== null) {
        if ('_id' in value) {
            return (value as { _id: unknown })._id;
        }
        if ('id' in value) {
            return (value as { id: unknown }).id;
        }
    }
    return value ?? null;
}

function isSet(value: unknown): boolean {

    if (value === null || value === undefined || value === '') {
        return false;
    }
    return !Array.isArray(value) || value.length > 0;
}

function textOf(value: unknown): string {

    if (value === null || value === undefined) {
        return '';
    }
    if (Array.isArray(value)) {
        return value.map(textOf).join(', ');
    }
    if (typeof value === 'object') {
        const entity = value as { name?: unknown; label?: unknown; _id?: unknown; id?: unknown };
        return textOf(entity.name ?? entity.label ?? entity._id ?? entity.id ?? null);
    }
    return String(value);
}

function readPath(root: unknown, path: string | undefined): unknown {

    if (!path) {
        return root ?? null;
    }
    let current = root;
    for (const part of path.split('.')) {
        if (typeof current !== 'object' || current === null || !Object.hasOwn(current, part)) {
            return null;
        }
        current = (current as Record<string, unknown>)[part];
    }
    return current ?? null;
}

function describe(value: WorkflowValue): string {

    switch (value.source) {
        case WorkflowValueSource.Literal:
            return JSON.stringify(value.value);
        case WorkflowValueSource.List:
            return `[${value.items.map(describe).join(', ')}]`;
        case WorkflowValueSource.Now:
            return 'now';
        case WorkflowValueSource.Trigger:
            return value.path;
        case WorkflowValueSource.Step:
            return value.path
                ? `steps.${value.stepId}.${value.path}`
                : `steps.${value.stepId}`;
        case WorkflowValueSource.Item:
            return value.path
                ? `item.${value.path}`
                : 'item';
        case WorkflowValueSource.Text:
            return 'text';
        case WorkflowValueSource.Setting:
            return value.path
                ? `settings.${value.key}.${value.path}`
                : `settings.${value.key}`;
        case WorkflowValueSource.Flag:
            return `flags.${value.flag}`;
        case WorkflowValueSource.Variable:
            return `vars.${value.name}`;
    }
}
